import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import VideoCard from "@/components/feature/VideoCard";
import ScrollTop from "@/components/feature/ScrollTop";
import { useSeo } from "@/hooks/useSeo";
import { videos, hotTopics } from "@/mocks/videos";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") || "").trim();
  const [keyword, setKeyword] = useState(q);

  const results = useMemo(() => {
    if (!q) return [];
    const kw = q.toLowerCase();
    return videos.filter(
      (v) =>
        v.title.toLowerCase().includes(kw) ||
        v.summary.toLowerCase().includes(kw) ||
        v.tags.some((t) => t.toLowerCase().includes(kw)) ||
        v.categoryName.toLowerCase().includes(kw)
    );
  }, [q]);

  useSeo({
    title: q ? `${q} 的搜索结果 - 吃瓜视频网` : "搜索 - 吃瓜视频网",
    description: q
      ? `吃瓜视频网关于“${q}”的搜索结果，为您整理相关娱乐八卦、圈内速递、明星动态、综艺片段、影视花絮与红人视频。`
      : "在吃瓜视频网搜索你感兴趣的明星、节目或关键词，快速找到想看的内容。",
    keywords: "吃瓜视频网,搜索,娱乐圈资讯",
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const k = keyword.trim();
    setParams({ q: k });
  };

  return (
    <div className="min-h-screen">
      <Header />

      <main className="mx-auto max-w-7xl px-4 md:px-6 pt-6">
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">搜索</span>
        </nav>

        {/* search box */}
        <form onSubmit={submit} className="max-w-2xl">
          <label htmlFor="search-input" className="block text-sm text-foreground-700 mb-2">搜索吃瓜内容</label>
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center bg-background-100 border border-background-200 rounded-full overflow-hidden focus-within:border-primary-400">
              <i className="ri-search-line text-lg text-foreground-400 pl-4"></i>
              <input
                id="search-input"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入明星、节目或关键词"
                className="flex-1 bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-foreground-400"
              />
            </div>
            <button type="submit" className="whitespace-nowrap px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600">
              搜索
            </button>
          </div>
        </form>

        {q ? (
          <section className="mt-8">
            <h1 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">
              关于“{q}”的搜索结果 <span className="text-sm text-foreground-500">({results.length} 条)</span>
            </h1>
            {results.length === 0 ? (
              <div className="bg-background-100 border border-background-200 rounded-lg p-12 text-center">
                <i className="ri-file-search-line text-5xl text-foreground-300"></i>
                <p className="mt-4 text-foreground-600">没有找到与“{q}”相关的内容，换个关键词试试吧。</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {results.map((v) => (
                  <VideoCard key={v.slug} video={v} />
                ))}
              </div>
            )}
          </section>
        ) : (
          <section className="mt-8">
            <h1 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5 flex items-center gap-2">
              <i className="ri-fire-line text-accent-500"></i>大家都在搜
            </h1>
            <div className="flex flex-wrap gap-2">
              {hotTopics.map((t) => (
                <Link
                  key={t}
                  to={`/search?q=${encodeURIComponent(t)}`}
                  className="px-4 py-2 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 whitespace-nowrap"
                >
                  {t}
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
      <ScrollTop />
    </div>
  );
}